import React, { useEffect, useState } from 'react'
import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  sourceFilesAtom,
  driveDirectoryAtom,
  customRegexAtom,
  customRegexFormatAtom,
  generatedMetaStructureAtom,
  generatedSimpleTreeAtom,
} from './atoms'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Input,
  InputLabel,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ExpandMore, Update } from '@mui/icons-material'

import { GoogleLoginButton, googleUserAtom } from 'infrastructure/googleDrive'
import GDriveMatchingTable from 'components/tree/googleApis/components/GDriveMatchingTable'
import { generateRows } from './utilities/utility'
import { GDriveMatchingTableRow } from 'infrastructure/googleDrive'
import { MetaStructureVisor } from './components/MetaStructureVisor/MetaStructureVisor'
import { IngestableJson } from '../IngestableJson'
import { ParseAll } from './components/ParseAll'
import GradeSelector from './components/GradeSelector/GradeSelector'
import { StructureRegexComposer } from './components/StructureRegexComposer'
import SimplifiedTreeGenerator from './components/SimplifiedTreeGenerator/SimplifiedTreeGenerator'
import SubjectSelector from './components/SubjectSelector/SubjectSelector'
import { AttributionSelector } from 'shared/ui'
import { selectedAttributionAtom } from './components/AttributionSelector/attribution.atom'
import { gradeAtom } from './components/GradeSelector/grade.atom'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'

export function Ingest() {
  const [rows, setRows] = useState<GDriveMatchingTableRow[]>([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const setFolderId = useSetAtom(driveDirectoryAtom)
  const { credential } = useAtomValue(googleUserAtom)
  const customFormat = useAtomValue(customRegexFormatAtom)
  const customRegex = useAtomValue(customRegexAtom)
  const metaStructure = useAtomValue(generatedMetaStructureAtom)
  const [ingestDisabled, setIngestDisabled] = React.useState(true)
  const [course] = useAtom(generatedSimpleTreeAtom)
  const [grade, setGrade] = useAtom(gradeAtom)
  const [attribution, setAttribution] = useAtom(selectedAttributionAtom)

  const files = useAtomValue(sourceFilesAtom)

  useEffect(() => {
    setLoadingFiles(false)
  }, [rows, loadingFiles])

  useEffect(() => {
    console.log('Updated course', course)
    setIngestDisabled(!course)
  }, [course])

  return (
    <>
      <Box>
        <h2>AI Generated curricula import</h2>
        <Box padding="20px" style={{ backgroundColor: '#EFFBFB' }}>
          This page uses the given Google Drive folder to generate a simplified
          tree. To later be generated into a course. Use the options on the left
          to import files. Options on the right to customize course.
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {!credential?.accessToken && (
            <Box>
              <GoogleLoginButton hideWhenLoggedIn={true} />
            </Box>
          )}
          <Box>
            <InputLabel>Google Drive Folder ID/URL</InputLabel>
            <Input
              placeholder="Enter Google Drive Folder ID/URL"
              onChange={(e) => setFolderId(e.target.value)}
              fullWidth={true}
            />
          </Box>
          <br />
          <Divider variant="fullWidth" />
          <br />
          <Box>
            <StructureRegexComposer />
          </Box>
          <Box>
            <LoadingButton
              variant="contained"
              onClick={() => {
                setLoadingFiles(true)
                if (files) {
                  setRows(generateRows(files, customRegex, customFormat))
                }
              }}
              loading={loadingFiles}
              color="primary"
              startIcon={<Update />}
            >
              Load Files
            </LoadingButton>
            <ParseAll rows={rows} />
          </Box>
          <Box padding="10px">
            <Box></Box>
          </Box>
          <Box padding="10px">
            <Accordion key={'matching-table-accordion'}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <h3>Files that matched the format</h3>
              </AccordionSummary>
              <AccordionDetails>
                {rows.length > 0 && <GDriveMatchingTable rows={rows} />}
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box padding="10px">
            <Accordion key={'parsed-content-accordion'}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1a-content"
                id="panel2a-header"
              >
                <h3>Parsed content</h3>
              </AccordionSummary>
              <AccordionDetails>
                Grade detected: {metaStructure.grade}
                {metaStructure.lessons.length > 0 && <MetaStructureVisor />}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box padding="10px">
            <Accordion key={'simplified-tree-accordion'}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel3a-content"
                id="panel3a-header"
              >
                <h3>Simplified tree</h3>
              </AccordionSummary>
              <AccordionDetails>
                <GradeSelector currentValue={grade} onChange={setGrade} />
                <SubjectSelector />
                <AttributionSelector
                  value={attribution}
                  onChange={setAttribution}
                />
                <SimplifiedTreeGenerator />
                <IngestableJson
                  json={course}
                  title={'Parsed Data'}
                  disabled={ingestDisabled}
                  key={'ingestable-component'}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
